/**
 * The Edit board modal's state, built from what the caller has (Boards
 * Redesign list 5, 12.10.2026). Pure, so the one rule that loses data when it
 * breaks is pinned by a unit test:
 *
 *   A GRID SUMMARY CARRIES NO PALETTE. Seeding the label editor from one would
 *   open it empty, and saving would write `labels: []` over every label on the
 *   board. So without the assembled board `labels` is `null` ("not read yet"),
 *   the editor says it is loading, and a save with `labels === null` keeps the
 *   board's own palette (`labelsForSave`).
 *
 *   The same holds for "Show completed tasks": a summary does not carry it, so
 *   until the board is read (`loaded`) and unless the switch was touched, a
 *   save keeps the board's own value (`showDoneForSave`). "Progress counts in
 *   column headers" follows the identical rule (`showColumnProgressForSave`).
 */
import { boardEditStats } from './boardEditStats';

/**
 * `summary` is whatever was clicked (a grid summary or the open board);
 * `full` the assembled board when one is in memory, else null. `scopeKeyOf`
 * is boardsStore.boardScopeKey, passed in so this module stays store-free.
 */
export function editModalFromBoard(summary, full, { gradient, scopeKeyOf } = {}) {
  const b = summary || {};
  return {
    mode: 'edit',
    id: b.id,
    name: b.name,
    // The name the board is saved under, for the delete confirm — the field
    // above may already hold an unsaved rename.
    savedName: b.name,
    description: b.description || '',
    color: b.color || (gradient ? gradient() : null),
    showDoneTasks: (full || b).showDoneTasks !== false,
    showColumnProgress: (full || b).showColumnProgress !== false,
    loaded: !!full,
    labels: full ? full.labels || [] : null,
    stats: full ? boardEditStats(full) : null,
    // The task-key prefix field (taskKeyPrefixEdit.js): the board's prefix, or
    // '' while unknown — a blank field saves as "no change".
    keyPrefix: (full || b).keyPrefix || '',
    savedKeyPrefix: (full || b).keyPrefix || null,
    closeFromCommits: (full || b).closeFromCommits === true,
    // Visibility isn't changed from the edit modal (use Share/Make private);
    // preserve it so the save routes to the right scope.
    visibility: b.visibility === 'team' ? 'team' : 'private',
    teamId: b.teamId != null ? String(b.teamId) : '',
    // The scope a delete from the footer tombstones: a summary knows it, the
    // assembled board derives it (team scope / device / personal).
    scopeKey: b.scopeKey || (scopeKeyOf ? scopeKeyOf(full || b) : undefined),
  };
}

/**
 * Fill a modal opened from a summary once the board has been read. Only while
 * it is still the same board and the palette is still unread — a palette the
 * user already started editing is never replaced.
 */
export function withLoadedBoard(modal, full) {
  if (!modal || !full || modal.id !== full.id) return modal;
  return {
    ...modal,
    loaded: true,
    labels: modal.labels == null ? full.labels || [] : modal.labels,
    keyPrefix: modal.keyPrefixTouched ? modal.keyPrefix : full.keyPrefix || modal.keyPrefix || '',
    savedKeyPrefix: full.keyPrefix || modal.savedKeyPrefix || null,
    closeFromCommits: modal.closeFromCommitsTouched ? modal.closeFromCommits : full.closeFromCommits === true,
    showDoneTasks: modal.loaded || modal.showDoneTouched ? modal.showDoneTasks : full.showDoneTasks !== false,
    showColumnProgress:
      modal.loaded || modal.showColumnProgressTouched ? modal.showColumnProgress !== false : full.showColumnProgress !== false,
    stats: boardEditStats(full),
  };
}

/** The palette a save writes: the edited one, or the board's own when the
 *  editor never got to read it. */
export function labelsForSave(modalLabels, base) {
  if (modalLabels == null) return Array.isArray(base?.labels) ? base.labels : [];
  return modalLabels;
}

/** The show-completed flag a save writes (see the module note). */
export function showDoneForSave(modal, base) {
  if (modal?.loaded || modal?.showDoneTouched) return modal.showDoneTasks !== false;
  return base?.showDoneTasks !== false;
}

/** The progress-counts flag a save writes — the same rule as showDoneForSave:
 *  the switch, once the board was read or the switch touched; else the board's
 *  own value. The UI sets `showColumnProgressTouched` when the switch moves. */
export function showColumnProgressForSave(modal, base) {
  if (modal?.loaded || modal?.showColumnProgressTouched) return modal.showColumnProgress !== false;
  return base?.showColumnProgress !== false;
}

/**
 * "Close tasks from commits" as a save writes it: the switch once touched (or
 * the board read), else the board's own value. Switching it ON stamps a new
 * `since`, so commits made before — or while it was off — never close a task.
 */
export function closeFromCommitsForSave(modal, base, now = new Date().toISOString()) {
  const on = modal?.closeFromCommitsTouched || modal?.loaded ? modal.closeFromCommits === true : base?.closeFromCommits === true;
  if (!on) return { closeFromCommits: false, closeFromCommitsSince: base?.closeFromCommitsSince || null };
  if (base?.closeFromCommits === true && base?.closeFromCommitsSince) return { closeFromCommits: true, closeFromCommitsSince: base.closeFromCommitsSince };
  return { closeFromCommits: true, closeFromCommitsSince: now };
}

/** The target the delete confirm needs, from the open modal. */
export function deleteTargetFromModal(modal) {
  if (!modal || modal.mode !== 'edit' || !modal.id) return null;
  return {
    id: modal.id,
    name: modal.savedName ?? modal.name,
    scopeKey: modal.scopeKey,
    visibility: modal.visibility,
    teamId: modal.visibility === 'team' && modal.teamId !== '' ? modal.teamId : null,
  };
}
